import { useState } from 'react';
import { Box, Grid, Typography, Dialog } from '@mui/material';
import { useTranslation } from 'react-i18next';

import StatsCards from '../components/StatsCards';
import ReservationTabs from '../components/ReservationTabs';
import ReservationTable from '../components/ReservationTable';
import ReservationModal from '../components/ReservationModal';
import { useReservationManagement } from '../hooks/useReservationManagement';
import ManualReservation from '../../../components/manualReservation_v2';
import type { ITaskManagementDashboardItem } from '../types/reservations.types';

const TaskManagementView = () => {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedListItemCheckIn, setSelectedListItemCheckIn] = useState<string | undefined>();
  const [selectedListItemCheckOut, setSelectedListItemCheckOut] = useState<string | undefined>();
  const [manualReservationOpen, setManualReservationOpen] = useState(false);
  const [selectedVacantProperty, setSelectedVacantProperty] = useState<ITaskManagementDashboardItem | null>(null);

  const {
    activeTab,
    setActiveTab,
    handleTabChange,
    activePage,
    setActivePage,
    perPage,
    setPerPage,
    stats,
    statsLoading,
    reservations,
    totalRecords,
    reservationsLoading,
    selectedReservationId,
    setSelectedReservationId,
    searchFilter,
    setSearchFilter,
    bookingStatusFilter,
    setBookingStatusFilter,
    propertyIdFilter,
    setPropertyIdFilter,
    dashboard,
    refetchDashboard,
    handleConfirmCheckout,
    isConfirmingCheckout,
  } = useReservationManagement();

  const handleCreateDirectBooking = (item: ITaskManagementDashboardItem) => {
    setSelectedVacantProperty(item);
    setManualReservationOpen(true);
  };

  return (
    <Grid
      container
      spacing={2}
    >
      {/* Page Header */}
      <Grid item xs={12}>
        <Typography
          sx={{
            fontFamily: 'Tajawal, sans-serif',
            fontWeight: 700,
            fontSize: '20px',
            color: '#130080',
            textAlign: 'start',
          }}
        >
          {t('TaskManagemnt.title')}
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Tajawal, sans-serif',
            fontWeight: 400,
            fontSize: '16px',
            color: '#ADBAC7',
            pt: '4px',
            textAlign: 'start',
          }}
        >
          {t('TaskManagemnt.subTitle')}
        </Typography>
      </Grid>

      {/* Stats Cards */}
      <Grid item xs={12}>
        {statsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <div
              className="animate-spin rounded-full h-8 w-8"
              style={{ border: '2px solid #E8E8EA', borderTopColor: '#5B3FFF' }}
            />
          </Box>
        ) : (
          <StatsCards
            checkInTodayCount={stats?.checkInTodayCount ?? 0}
            checkOutTodayCount={stats?.checkOutTodayCount ?? 0}
            upcomingCount={stats?.upcomingCount ?? 0}
            vacantTodayCount={stats?.vacantTodayCount ?? 0}
          />
        )}
      </Grid>

      {/* Tabs */}
      <Grid item xs={12}>
        <ReservationTabs activeTab={activeTab} onChange={handleTabChange} />
      </Grid>

      {/* Table Section */}
      <Grid item xs={12}>
        <ReservationTable
        activeTab={activeTab}
        items={reservations}
        totalCount={totalRecords}
        isLoading={reservationsLoading}
        activePage={activePage}
        perPage={perPage}
        onPageChange={setActivePage}
        onPerPageChange={setPerPage}
        onRowClick={(item) => {
          if (item.id && (activeTab === 'upcoming' || activeTab === 'checkin_today' || activeTab === 'checkout_today')) {
            setSelectedReservationId(item.id);
            setSelectedListItemCheckIn(item.checkIn || undefined);
            setSelectedListItemCheckOut(item.checkOut || undefined);
            setModalOpen(true);
          }
        }}
        onConfirmCheckout={handleConfirmCheckout}
        isConfirmingCheckout={isConfirmingCheckout}
        onCreateDirectBooking={activeTab === 'vacant' ? handleCreateDirectBooking : undefined}
        searchValue={searchFilter}
        onSearchChange={(val: string) => { setSearchFilter(val); setActivePage(1); }}
        bookingStatusFilter={bookingStatusFilter}
        onBookingStatusChange={(val: string | undefined) => { setBookingStatusFilter(val); setActivePage(1); }}
        propertyIdFilter={propertyIdFilter}
        onPropertyIdChange={(val: number | undefined) => { setPropertyIdFilter(val); setActivePage(1); }}
        />
      </Grid>

      {/* Upcoming Edit Modal */}
      <ReservationModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedReservationId(undefined);
          setSelectedListItemCheckIn(undefined);
          setSelectedListItemCheckOut(undefined);
        }}
        reservationId={selectedReservationId}
        tabType={activeTab === 'checkin_today' ? 'checkin' : activeTab === 'checkout_today' ? 'checkout' : 'upcoming'}
        listItemCheckIn={selectedListItemCheckIn}
        listItemCheckOut={selectedListItemCheckOut}
      />

      {/* Manual Reservation Modal */}
      <Dialog
        open={manualReservationOpen}
        onClose={() => {
          setManualReservationOpen(false);
          setSelectedVacantProperty(null);
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            maxHeight: '90vh',
          },
        }}
      >
        <ManualReservation
          preselectedProperty={selectedVacantProperty ? {
            propertyId: String(selectedVacantProperty.propertyId),
            propertyName: selectedVacantProperty.propertyName,
          } : undefined}
          onClose={() => {
            setManualReservationOpen(false);
            setSelectedVacantProperty(null);
            refetchDashboard();
          }}
        />
      </Dialog>
    </Grid>
  );
};

export default TaskManagementView;
